import { z } from "zod";
import type { SocketType } from "../types";

/**
 * Get the Zod type name from a schema using Zod v4's traits system
 */
export function getZodTypeName(schema: z.ZodType): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const traits = (schema as any)._zod?.traits as Set<string> | undefined;
  if (!traits) return undefined;

  // Find the first trait that starts with "Zod" but not "ZodType"
  for (const trait of traits) {
    if (trait.startsWith("Zod") && trait !== "ZodType") {
      return trait;
    }
  }
  return undefined;
}

/**
 * Get the inner/wrapped type from optional, nullable, default, or pipe schemas
 */
export function getInnerType(schema: z.ZodType): z.ZodType | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const def = (schema as any)._zod?.def;
  // Zod v4 uses innerType for optional/nullable/default
  if (def?.innerType) return def.innerType;
  // Zod v4 uses 'in' for pipe/effects
  if (def?.in) return def.in;
  return undefined;
}

/**
 * Infer the socket type from a Zod field schema
 *
 * This utility examines a Zod schema and determines the appropriate
 * socket type for workflow step inputs/outputs. It handles:
 * - Primitive types (string, number, boolean, date)
 * - UUIDs (detected via Zod checks)
 * - Entity references (objects with entityType + entityId)
 * - Arrays (including typed entity reference arrays for fanout)
 * - Unions, enums, literals
 * - Optional/nullable wrappers
 */
export function inferSocketTypeFromZod(fieldSchema: z.ZodType): SocketType {
  const typeName = getZodTypeName(fieldSchema);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const def = (fieldSchema as any)._zod?.def;

  // Handle optional/nullable wrappers - unwrap and recurse
  if (typeName === "ZodOptional" || typeName === "ZodNullable") {
    const inner = getInnerType(fieldSchema);
    if (inner) return inferSocketTypeFromZod(inner);
  }

  // Handle default wrappers
  if (typeName === "ZodDefault") {
    const inner = getInnerType(fieldSchema);
    if (inner) return inferSocketTypeFromZod(inner);
  }

  // Handle effects/pipe (like .transform()) - Zod v4 uses ZodPipe
  if (typeName === "ZodEffects" || typeName === "ZodPipe") {
    const inner = getInnerType(fieldSchema);
    if (inner) return inferSocketTypeFromZod(inner);
  }

  // Primitive types
  if (typeName === "ZodString") {
    // Check for UUID format - Zod v4 stores format directly on schema or in checks
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const format = (fieldSchema as any).format;
    if (format === "uuid") return "uuid";

    // Also check checks array for UUID
    const checks = def?.checks || [];
    const isUuid = checks.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (check: any) => check.format === "uuid" || check.kind === "uuid",
    );
    if (isUuid) return "uuid";
    return "string";
  }

  if (typeName === "ZodNumber") {
    return "number";
  }

  if (typeName === "ZodBoolean") {
    return "boolean";
  }

  if (typeName === "ZodDate") {
    return "date";
  }

  // Array types - Zod v4 uses 'element' instead of 'type'
  if (typeName === "ZodArray") {
    const elementSchema = def?.element;
    if (elementSchema) {
      const elementType = inferSocketTypeFromZod(elementSchema);
      if (elementType === "string") return "array<string>";
      if (elementType === "number") return "array<number>";
      // Handle typed entity references like "entity_reference:CORE_DOCUMENT"
      if (elementType.startsWith("entity_reference")) {
        return `array<${elementType}>` as SocketType;
      }
    }
    // Default to json for complex arrays
    return "json";
  }

  // Object types - typically entity_reference or json
  if (typeName === "ZodObject") {
    // Check if it looks like an entity reference (has entityType and entityId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape = (fieldSchema as any).shape;
    if (shape && shape.entityType && shape.entityId) {
      // Try to extract the specific entity type from the literal
      const entityTypeSchema = shape.entityType;
      const entityTypeName = getZodTypeName(entityTypeSchema);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entityTypeDef = (entityTypeSchema as any)?._zod?.def;

      if (entityTypeName === "ZodLiteral" && entityTypeDef?.values?.[0]) {
        // Zod v4 stores literal values in 'values' array
        return `entity_reference:${entityTypeDef.values[0]}` as SocketType;
      }
      // Generic entity reference
      return "entity_reference";
    }
    // Otherwise treat as JSON
    return "json";
  }

  // Enum types - treat as string (Zod v4 uses ZodEnum for both enum and nativeEnum)
  if (typeName === "ZodEnum") {
    return "string";
  }

  // Union types - check if it's a simple string union
  if (typeName === "ZodUnion") {
    const options = def?.options || [];
    const allStrings = options.every(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (opt: any) => {
        const optTypeName = getZodTypeName(opt);
        return optTypeName === "ZodLiteral" || optTypeName === "ZodString";
      },
    );
    if (allStrings) return "string";
    return "json";
  }

  // Literal types - Zod v4 stores value in 'values' array
  if (typeName === "ZodLiteral") {
    const value = def?.values?.[0];
    if (typeof value === "string") return "string";
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "boolean";
    return "json";
  }

  // Record types - treat as json
  if (typeName === "ZodRecord") {
    return "json";
  }

  // Void/undefined - used for signals
  if (typeName === "ZodVoid" || typeName === "ZodUndefined") {
    return "signal";
  }

  // Default fallback
  return "any";
}

/**
 * Check if a Zod field schema represents an optional field
 *
 * Returns true if the schema is wrapped in ZodOptional, ZodNullable,
 * or ZodDefault (since having a default makes it effectively optional).
 */
export function isFieldOptional(fieldSchema: z.ZodType): boolean {
  const typeName = getZodTypeName(fieldSchema);
  if (typeName === "ZodOptional" || typeName === "ZodNullable") {
    return true;
  }
  if (typeName === "ZodDefault") {
    return true; // Has a default, so effectively optional
  }
  return false;
}

/**
 * Socket definition type for generated sockets
 */
export interface GeneratedSocket {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/**
 * Generate socket definitions from a Zod schema
 *
 * This converts a Zod object schema into an array of socket definitions,
 * inferring the socket type for each field. Used for both input and output
 * sockets in workflow step definitions.
 *
 * @param schema - The Zod schema to convert
 * @param socketKind - Whether these are "input" or "output" sockets (for descriptions)
 * @returns Array of socket definitions
 */
export function generateSocketsFromSchema(
  schema: z.ZodType,
  socketKind: "input" | "output",
): GeneratedSocket[] {
  // Unwrap optional/nullable/default/effects wrappers to get the actual type
  let unwrapped = schema;
  let typeName = getZodTypeName(unwrapped);

  while (
    typeName === "ZodOptional" ||
    typeName === "ZodNullable" ||
    typeName === "ZodDefault" ||
    typeName === "ZodEffects" ||
    typeName === "ZodPipe"
  ) {
    const inner = getInnerType(unwrapped);
    if (!inner) break;
    unwrapped = inner;
    typeName = getZodTypeName(unwrapped);
  }

  // For z.never() (event triggers with no inputs), return empty array
  if (typeName === "ZodNever") {
    return [];
  }

  // For z.object(), extract keys and infer types
  if (typeName === "ZodObject") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape = (unwrapped as any).shape;
    if (shape && typeof shape === "object") {
      return Object.entries(shape).map(([key, fieldSchema]) => {
        const inferredType = inferSocketTypeFromZod(fieldSchema as z.ZodType);
        const required = !isFieldOptional(fieldSchema as z.ZodType);

        return {
          id: key,
          name: key
            // Insert space before uppercase letters that follow lowercase letters
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            // Insert space before uppercase letters followed by lowercase (for acronyms)
            .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
            // Capitalize first letter
            .replace(/^./, (str) => str.toUpperCase()),
          type: inferredType,
          required,
          description: `${socketKind === "input" ? "Input" : "Output"} field: ${key}`,
        };
      });
    }
  }

  // For single-value schemas, return a single socket with inferred type
  const singleType = inferSocketTypeFromZod(unwrapped);
  return [
    {
      id: "value",
      name: "Value",
      type: singleType,
      required: true,
      description: `${socketKind === "input" ? "Input" : "Output"} value`,
    },
  ];
}
